/** @format */

export type ImportPdfSlot = "paper" | "markScheme" | "specification" | "paperCy" | "markSchemeCy";

export type ImportPdfProblemKey =
	| "pdfImport.needPaper"
	| "pdfImport.needMarkScheme"
	| "pdfImport.needSpecification"
	| "pdfImport.needEnglish"
	| "pdfImport.needWelsh"
	| "pdfImport.unconfirmed";

type ImportPdfKind = "paper" | "markScheme" | "specification";
type ImportPdfLanguage = "en" | "cy";

interface DetectedPdf {
	kind: ImportPdfKind;
	language: ImportPdfLanguage;
}

const MARK_EN = /\bMARK(?:ING)? SCHEME\b/;
const MARK_CY = /\bCYNLLUN(?:IAU)? MARCIO\b/;
const SPEC = /\b(?:SPECIFICATIONS?|MANYLEB(?:AU)?)\b/;
const PAPER_EN = /\b(?:ADDITIONAL MATERIALS|INSTRUCTIONS (?:TO|FOR) CANDIDATES|INFORMATION FOR CANDIDATES)\b/;
const PAPER_CY = /\b(?:DEUNYDD(?:IAU)? YCHWANEGOL|CYFARWYDDIADAU I(?:'R)? YMGEISWYR|GWYBODAETH I YMGEISWYR)\b/;
const PAPER_CODE = /(?<![\w-])(?:[A-Z]\d{2}-)?(?:[A-Z]\d{3}|\d{4})[A-Z]{1,2}[A-Z0-9]{2}-\d(?![\w-])/;
const WELSH = /\b(?:TGAU|CYMRAEG|CYNLLUN|MANYLEB|DEUNYDDIAU|CYFARWYDDIADAU)\b/;
const ENGLISH_QUAL = /\b(?:GCSE|GCE|A LEVEL)\b/;

const EXPECTED: Record<ImportPdfSlot, { kind: ImportPdfKind; language: ImportPdfLanguage | null }> = {
	paper: { kind: "paper", language: "en" },
	paperCy: { kind: "paper", language: "cy" },
	markScheme: { kind: "markScheme", language: "en" },
	markSchemeCy: { kind: "markScheme", language: "cy" },
	specification: { kind: "specification", language: null },
};

const NEED_KIND: Record<ImportPdfKind, ImportPdfProblemKey> = {
	paper: "pdfImport.needPaper",
	markScheme: "pdfImport.needMarkScheme",
	specification: "pdfImport.needSpecification",
};

const normalise = (text: string): string => text.replace(/[–—]/g, "-").replace(/\s+/g, " ").toUpperCase();

interface PdfSignals {
	markEn: boolean;
	markCy: boolean;
	spec: boolean;
	paperPhraseEn: boolean;
	paperPhraseCy: boolean;
	weakEn: boolean;
	weakCy: boolean;
}

/** Read the phrases on the page. A paper code alone does not reject another slot. */
const signalsOf = (text: string): PdfSignals => {
	const page = normalise(text);
	const hasMarkEn = MARK_EN.test(page);
	const hasMarkCy = MARK_CY.test(page);
	const hasSpec = SPEC.test(page);
	const hasPaperPhraseEn = PAPER_EN.test(page);
	const hasPaperPhraseCy = PAPER_CY.test(page);
	const hasCode = PAPER_CODE.test(page);
	const hasWelsh = WELSH.test(page);
	const hasEnglishQual = ENGLISH_QUAL.test(page);
	const isNamed = hasMarkEn || hasMarkCy || hasSpec || hasPaperPhraseEn || hasPaperPhraseCy;
	return {
		markEn: hasMarkEn,
		markCy: hasMarkCy,
		spec: hasSpec,
		paperPhraseEn: hasPaperPhraseEn,
		paperPhraseCy: hasPaperPhraseCy,
		weakEn: !isNamed && hasCode && hasEnglishQual,
		weakCy: !isNamed && hasCode && hasWelsh,
	};
};

/** Read one label from first-page text. Null when the page does not say. */
export const detectImportPdf = (text: string): DetectedPdf | null => {
	const signals = signalsOf(text);
	if (signals.markEn) {
		return { kind: "markScheme", language: "en" };
	}
	if (signals.markCy) {
		return { kind: "markScheme", language: "cy" };
	}
	if (signals.spec) {
		return { kind: "specification", language: "en" };
	}
	if (signals.paperPhraseEn || signals.weakEn) {
		return { kind: "paper", language: "en" };
	}
	if (signals.paperPhraseCy || signals.weakCy) {
		return { kind: "paper", language: "cy" };
	}
	return null;
};

const languageProblem = (
	expected: ImportPdfLanguage,
	hasExpected: boolean,
	hasOther: boolean,
): ImportPdfProblemKey | null => {
	if (hasExpected) {
		return null;
	}
	if (hasOther) {
		return expected === "en" ? "pdfImport.needEnglish" : "pdfImport.needWelsh";
	}
	return null;
};

/**
 * Return a message key when the first page clearly belongs in another slot.
 * A page that names both languages is allowed in either language slot.
 * A page that does not name its kind is allowed.
 */
export const importPdfProblem = (text: string, slot: ImportPdfSlot): ImportPdfProblemKey | null => {
	const signals = signalsOf(text);
	const expected = EXPECTED[slot];
	const hasPaperEn = signals.paperPhraseEn || signals.weakEn;
	const hasPaperCy = signals.paperPhraseCy || signals.weakCy;
	const isMark = signals.markEn || signals.markCy;
	if (expected.kind === "paper") {
		if (hasPaperEn || hasPaperCy) {
			if (!expected.language) {
				return null;
			}
			const hasExpected = expected.language === "en" ? hasPaperEn : hasPaperCy;
			const hasOther = expected.language === "en" ? hasPaperCy : hasPaperEn;
			return languageProblem(expected.language, hasExpected, hasOther);
		}
		return isMark || signals.spec ? NEED_KIND.paper : null;
	}
	if (expected.kind === "markScheme") {
		if (isMark) {
			if (!expected.language) {
				return null;
			}
			const hasExpected = expected.language === "en" ? signals.markEn : signals.markCy;
			const hasOther = expected.language === "en" ? signals.markCy : signals.markEn;
			return languageProblem(expected.language, hasExpected, hasOther);
		}
		return signals.paperPhraseEn || signals.paperPhraseCy || signals.spec ? NEED_KIND.markScheme : null;
	}
	if (signals.spec) {
		return null;
	}
	return signals.paperPhraseEn || signals.paperPhraseCy || isMark ? NEED_KIND.specification : null;
};
